<script setup lang="ts">
import MessageList from '@/components/assistant/chat/MessageList.vue'
import type {CreditUsage} from '@/client/mentor/CreditUsageClient'
import type {Message} from '@/client/mentor/MessagesClient'
import {PENDING_TIMEOUT_DELAYS_MS} from '@/components/assistant/constants'
import {ref, watch} from 'vue'
import {useTimeoutFn} from '@vueuse/core'

const emit = defineEmits<{
  selectSuggestion: [suggestion: string]
}>()

const props = defineProps<{
  companyId: number
  creditUsage?: CreditUsage
  fetchNextPage: () => Promise<unknown>
  hasCreditsLeft: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  isSending: boolean
  lastPendingMessageId?: number
  messages: Message[]
  updateMessageById: (messageId: number) => Promise<unknown>
}>()

let prevLastPendingMessageId: number | null = null
const pendingTimeoutDelayMs = ref(PENDING_TIMEOUT_DELAYS_MS[0])

const {start, stop} = useTimeoutFn(
  async () => {
    if (props.isSending || !props.lastPendingMessageId) return

    await props.updateMessageById(props.lastPendingMessageId)

    increasePendingTimeoutDelay()
    start()
  },
  pendingTimeoutDelayMs,
  {immediate: false}
)

watch(
  () => [props.isSending, props.lastPendingMessageId],
  async () => {
    if (props.isSending) {
      stop()
      return
    }

    if (!props.lastPendingMessageId) {
      stop()
      prevLastPendingMessageId = null
      resetPendingTimeoutDelay()
      return
    }

    if (props.lastPendingMessageId && prevLastPendingMessageId !== props.lastPendingMessageId) {
      prevLastPendingMessageId = props.lastPendingMessageId
      resetPendingTimeoutDelay()
      await props.updateMessageById(props.lastPendingMessageId)
    }

    if (props.lastPendingMessageId) {
      start()
    } else {
      stop()
    }
  },
  {immediate: true}
)

function increasePendingTimeoutDelay() {
  const currentDelayIndex = PENDING_TIMEOUT_DELAYS_MS.indexOf(pendingTimeoutDelayMs.value)
  const safeDelayIndex = currentDelayIndex >= 0 ? currentDelayIndex : 0
  const nextDelayIndex = Math.min(safeDelayIndex + 1, PENDING_TIMEOUT_DELAYS_MS.length - 1)
  pendingTimeoutDelayMs.value = PENDING_TIMEOUT_DELAYS_MS[nextDelayIndex]
}

function resetPendingTimeoutDelay() {
  pendingTimeoutDelayMs.value = PENDING_TIMEOUT_DELAYS_MS[0]
}
</script>

<template>
  <MessageList
    :company-id="companyId"
    :credit-usage="creditUsage"
    :disabled="isSending || !hasCreditsLeft || !!lastPendingMessageId"
    :fetch-next-page="fetchNextPage"
    :has-next-page="hasNextPage"
    :is-fetching-next-page="isFetchingNextPage"
    :is-loading="isLoading"
    :messages="messages"
    @select-suggestion="emit('selectSuggestion', $event)"
  />
</template>
